import { Box } from "@mui/material";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";

import type { Seed } from "@/features/GenLab/components/freeFormSeeds";
import type { SeedType } from "@/features/GenLab/components/pickedSeeds";
import type { SidePanel } from "@/features/GenLab/components/RightPanel";
import type { ProductSelection } from "@/features/ProductSearch";
import type { SeedMessage } from "@/types/creativesphere";

import {
  useCreateSeed,
  useDeleteSeed,
  useGenerations,
  useSeedPresets,
  useSeeds,
} from "@/api/creativesphere";
import { castingCodec } from "@/api/creativesphere/codecs/casting";
import { compositionCodec } from "@/api/creativesphere/codecs/composition";
import { photographyCodec } from "@/api/creativesphere/codecs/photography";
import { sceneCodec } from "@/api/creativesphere/codecs/scene";
import { Canvas } from "@/features/GenLab/components/Canvas";
import { parseFormatRatio } from "@/features/GenLab/components/compositionPresets";
import { FreeFormBrowser } from "@/features/GenLab/components/FreeFormBrowser";
import { LeftPanel } from "@/features/GenLab/components/LeftPanel";
import { generationToLibrarySingle } from "@/features/GenLab/components/libraryMapping";
import { LibrarySessionOverlay } from "@/features/GenLab/components/LibrarySessionOverlay";
import { pickedProductFromSelection } from "@/features/GenLab/components/productSelection";
import { RightPanel } from "@/features/GenLab/components/RightPanel";
import { sortSeedsNewestFirst } from "@/features/GenLab/components/seedOrdering";
import { SelectedSeedsFooter } from "@/features/GenLab/components/SelectedSeedsFooter";
import { StudioHeaderActions } from "@/features/GenLab/components/StudioHeaderActions";
import { useStudioUiState } from "@/features/GenLab/components/studioUiStateContext";
import { buildMissingSeedsMessage } from "@/features/GenLab/components/useCanvasGeneration";
import { OrionPageHeader } from "@/orion/OrionComponents/OrionPageHeader";

export const Route = createFileRoute("/_auth/genlab/$siteId/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { siteId } = Route.useParams();
  const navigate = useNavigate();

  const photographyQuery = useSeeds(siteId, "photography");
  const presets = useMemo(
    () =>
      sortSeedsNewestFirst(photographyQuery.data?.seeds ?? []).map((seed) =>
        photographyCodec.fromSeed(seed),
      ),
    [photographyQuery.data],
  );
  const compositionQuery = useSeeds(siteId, "composition");
  const compositionPresets = useMemo(
    () =>
      sortSeedsNewestFirst(compositionQuery.data?.seeds ?? []).map((seed) =>
        compositionCodec.fromSeed(seed),
      ),
    [compositionQuery.data],
  );
  const sceneQuery = useSeeds(siteId, "scene");
  const sceneAssets = useMemo(
    () =>
      sortSeedsNewestFirst(sceneQuery.data?.seeds ?? []).map((seed) =>
        sceneCodec.fromSeed(seed),
      ),
    [sceneQuery.data],
  );
  const castingQuery = useSeeds(siteId, "casting");
  const castingAssets = useMemo(
    () =>
      sortSeedsNewestFirst(castingQuery.data?.seeds ?? []).map((seed) =>
        castingCodec.fromSeed(seed),
      ),
    [castingQuery.data],
  );

  const seedPresetsQuery = useSeedPresets();
  const presetSeeds = useMemo(
    () => sortSeedsNewestFirst(seedPresetsQuery.data?.seeds ?? []),
    [seedPresetsQuery.data],
  );
  const photographyPresetItems = useMemo(
    () =>
      presetSeeds
        .filter((s) => s.seed_type === "photography")
        .map((seed) => photographyCodec.fromSeed(seed)),
    [presetSeeds],
  );
  const compositionPresetItems = useMemo(
    () =>
      presetSeeds
        .filter((s) => s.seed_type === "composition")
        .map((seed) => compositionCodec.fromSeed(seed)),
    [presetSeeds],
  );
  const scenePresetItems = useMemo(
    () =>
      presetSeeds
        .filter((s) => s.seed_type === "scene")
        .map((seed) => sceneCodec.fromSeed(seed)),
    [presetSeeds],
  );
  const castingPresetItems = useMemo(
    () =>
      presetSeeds
        .filter((s) => s.seed_type === "casting")
        .map((seed) => castingCodec.fromSeed(seed)),
    [presetSeeds],
  );

  const libraryQuery = useGenerations(siteId);
  const libraryItems = useMemo(
    () => (libraryQuery.data?.generations ?? []).map(generationToLibrarySingle),
    [libraryQuery.data],
  );

  // Loaded seeds (site seeds + presets) keyed by slug, so a seed preview can
  // read the record the API returned rather than a codec projection of it.
  const seedsBySlug = useMemo(() => {
    const map: Record<string, SeedMessage> = {};
    for (const seed of [
      ...(photographyQuery.data?.seeds ?? []),
      ...(compositionQuery.data?.seeds ?? []),
      ...(sceneQuery.data?.seeds ?? []),
      ...(castingQuery.data?.seeds ?? []),
      ...presetSeeds,
    ]) {
      map[seed.slug] = seed;
    }
    return map;
  }, [
    photographyQuery.data,
    compositionQuery.data,
    sceneQuery.data,
    castingQuery.data,
    presetSeeds,
  ]);

  const createPhotography = useCreateSeed(siteId);
  const deletePhotographyMut = useDeleteSeed(siteId, "photography");
  const duplicatePreset = (id: string) => {
    const source = [...photographyPresetItems, ...presets].find(
      (p) => p.id === id,
    );
    if (!source) return;
    createPhotography.mutate({
      seed_type: "photography",
      name: `${source.name} Copy`,
      description: source.description,
      params: photographyCodec.toParams(source.settings),
    });
  };
  const deletePreset = (id: string) => deletePhotographyMut.mutate(id);

  const createComposition = useCreateSeed(siteId);
  const deleteCompositionMut = useDeleteSeed(siteId, "composition");
  const duplicateCompositionPreset = (id: string) => {
    const source = [...compositionPresetItems, ...compositionPresets].find(
      (p) => p.id === id,
    );
    if (!source) return;
    createComposition.mutate({
      seed_type: "composition",
      name: `${source.name} Copy`,
      params: compositionCodec.toParams({
        format: source.format,
        placement: source.placement,
      }),
      thumbnail_url: source.thumbnailUrl,
    });
  };
  const deleteCompositionPreset = (id: string) =>
    deleteCompositionMut.mutate(id);

  const createScene = useCreateSeed(siteId);
  const deleteSceneMut = useDeleteSeed(siteId, "scene");
  const duplicateSceneAsset = (id: string) => {
    const source = [...scenePresetItems, ...sceneAssets].find(
      (a) => a.id === id,
    );
    if (!source) return;
    createScene.mutate({
      seed_type: "scene",
      name: `${source.name} Copy`,
      params: sceneCodec.toParams({
        instructions: source.instructions,
        notes: source.notes,
      }),
      tags: source.tags,
      thumbnail_url: source.thumbnailUrl,
    });
  };
  const deleteSceneAsset = (id: string) => deleteSceneMut.mutate(id);

  const createCasting = useCreateSeed(siteId);
  const deleteCastingMut = useDeleteSeed(siteId, "casting");
  const duplicateCastingAsset = (id: string) => {
    const source = [...castingPresetItems, ...castingAssets].find(
      (a) => a.id === id,
    );
    if (!source) return;
    createCasting.mutate({
      seed_type: "casting",
      name: `${source.name} Copy`,
      params: castingCodec.toParams({
        instructions: source.instructions,
        notes: source.notes,
      }),
      tags: source.tags,
      thumbnail_url: source.thumbnailUrl,
    });
  };
  const deleteCastingAsset = (id: string) => deleteCastingMut.mutate(id);

  const {
    selectedModel,
    setSelectedModel,
    pickedSeeds,
    setPickedSeeds,
    activeTab,
    setActiveTab,
    activePanel,
    setActivePanel,
    leftPanelCollapsed,
    setLeftPanelCollapsed,
    librarySelection,
    setLibrarySelection,
    canvas,
  } = useStudioUiState();

  const canvasAspectRatio = parseFormatRatio(pickedSeeds.composition?.format);

  const handleSelectSeed = (seed: Seed) => {
    setPickedSeeds((prev) => ({
      ...prev,
      [seed.tab]: {
        type: seed.tab,
        name: seed.name,
        thumbnailUrl: seed.thumbnailUrl,
        id: seed.id,
        format: seed.format,
        placement: seed.placement,
        tags: seed.tags,
        instructions: seed.instructions,
        notes: seed.notes,
      },
    }));
  };

  const handleSelectProduct = (selection: ProductSelection) => {
    setPickedSeeds((prev) => ({
      ...prev,
      product: pickedProductFromSelection(selection),
    }));
  };

  const handleRemoveSeed = (type: SeedType) => {
    setPickedSeeds((prev) => {
      const next = { ...prev };
      delete next[type];
      return next;
    });
  };

  const handleTogglePanel = (panel: SidePanel) =>
    setActivePanel((prev) => (prev === panel ? null : panel));

  const handleEditSeed = (type: SeedType) => {
    setActiveTab(type);
  };

  const handleRender = () => {
    const willRender = canvas.readyToGenerate && !canvas.isGenerating;
    canvas.render();
    if (willRender) setLibrarySelection(null);
  };

  const selectedLibraryImageId =
    librarySelection?.mode === "single" ? librarySelection.item.image.id : null;

  const handleEditPreset = (preset: { id: string }) => {
    void navigate({
      to: "/genlab/$siteId/presets/edit/$presetId",
      params: { siteId, presetId: preset.id },
    });
  };

  const handleEditCompositionPreset = (preset: { id: string }) => {
    void navigate({
      to: "/genlab/$siteId/presets/composition/edit/$presetId",
      params: { siteId, presetId: preset.id },
    });
  };

  const handleEditSceneAsset = (asset: { id: string }) => {
    void navigate({
      to: "/genlab/$siteId/presets/scene/edit/$presetId",
      params: { siteId, presetId: asset.id },
    });
  };

  const handleEditCastingAsset = (asset: { id: string }) => {
    void navigate({
      to: "/genlab/$siteId/presets/casting/edit/$presetId",
      params: { siteId, presetId: asset.id },
    });
  };

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        flex: 1,
      }}
    >
      <OrionPageHeader
        paddingY={2}
        sx={{
          bgcolor: (theme) => theme.palette.background["paper-elevation-1"],
        }}
        breadcrumbs={[{ label: "Creativesphere" }]}
        title="Generation Lab"
        actions={
          <StudioHeaderActions
            readyToGenerate={canvas.readyToGenerate}
            renderBlockedHint={buildMissingSeedsMessage(canvas.missingSeeds)}
            onRender={handleRender}
            activePanel={activePanel}
            onTogglePanel={handleTogglePanel}
            isGenerating={canvas.isGenerating}
          />
        }
      />

      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "row",
          flex: 1,
          minHeight: 0,
        }}
      >
        <LeftPanel
          selectedModel={selectedModel}
          onModelChange={setSelectedModel}
          collapsed={leftPanelCollapsed}
          onToggleCollapsed={() => setLeftPanelCollapsed((prev) => !prev)}
          footer={
            <SelectedSeedsFooter
              seeds={pickedSeeds}
              onEditSeed={handleEditSeed}
              onRemoveSeed={handleRemoveSeed}
            />
          }
        >
          <FreeFormBrowser
            onSelectSeed={handleSelectSeed}
            activeTab={activeTab}
            onActiveTabChange={setActiveTab}
            selectedSeedId={pickedSeeds[activeTab]?.id}
            photographyPresets={presets}
            photographyPresetItems={photographyPresetItems}
            onDuplicatePreset={(preset) => duplicatePreset(preset.id)}
            onDeletePreset={(preset) => deletePreset(preset.id)}
            onEditPreset={handleEditPreset}
            compositionPresets={compositionPresets}
            compositionPresetItems={compositionPresetItems}
            onDuplicateCompositionPreset={(preset) =>
              duplicateCompositionPreset(preset.id)
            }
            onDeleteCompositionPreset={(preset) =>
              deleteCompositionPreset(preset.id)
            }
            onEditCompositionPreset={handleEditCompositionPreset}
            seedsBySlug={seedsBySlug}
            sceneAssets={sceneAssets}
            scenePresetItems={scenePresetItems}
            onDuplicateSceneAsset={(asset) => duplicateSceneAsset(asset.id)}
            onDeleteSceneAsset={(asset) => deleteSceneAsset(asset.id)}
            onEditSceneAsset={handleEditSceneAsset}
            castingAssets={castingAssets}
            castingPresetItems={castingPresetItems}
            onDuplicateCastingAsset={(asset) => duplicateCastingAsset(asset.id)}
            onDeleteCastingAsset={(asset) => deleteCastingAsset(asset.id)}
            onEditCastingAsset={handleEditCastingAsset}
            siteId={siteId}
            onSelectProduct={handleSelectProduct}
          />
        </LeftPanel>

        {librarySelection ? (
          <LibrarySessionOverlay
            selection={librarySelection}
            aspectRatio={canvasAspectRatio}
            onClose={() => setLibrarySelection(null)}
          />
        ) : (
          <Canvas
            status={canvas.status}
            result={canvas.result}
            aspectRatio={canvasAspectRatio}
          />
        )}

        <RightPanel
          activePanel={activePanel}
          onActivePanelChange={setActivePanel}
          libraryItems={libraryItems}
          libraryLoading={libraryQuery.isLoading}
          libraryError={
            libraryQuery.isError ? "Couldn't load your library." : null
          }
          selectedLibraryImageId={selectedLibraryImageId}
          onSelectLibrarySingle={(item) =>
            setLibrarySelection({ mode: "single", item })
          }
        />
      </Box>
    </Box>
  );
}
